import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from './components/Logo';

const LandingPage = ({ onEnter, onViewStory, darkMode, user }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`min-h-[100dvh] relative overflow-hidden ${
        darkMode
          ? 'bg-[#191919]'
          : 'bg-gradient-to-br from-purple-50 via-pink-50 to-cyan-50'
      }`}
    >
      {/* Background Orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={`absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-20 ${
            darkMode
              ? 'bg-gradient-to-r from-purple-600 to-pink-600'
              : 'bg-gradient-to-r from-purple-300 to-pink-300'
          } animate-pulse`}
        />
        <div
          className={`absolute bottom-20 right-10 w-72 h-72 rounded-full blur-3xl opacity-20 ${
            darkMode
              ? 'bg-gradient-to-r from-cyan-600 to-purple-600'
              : 'bg-gradient-to-r from-cyan-300 to-purple-300'
          } animate-pulse`}
          style={{ animationDelay: '1s' }}
        />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[100dvh] px-6 text-center">

        {/* CENTERED LOGO + BRANDING */}
        <div className="flex flex-col items-center justify-center flex-1 w-full">
          <div className="mb-6 sm:mb-8 w-full flex items-center justify-center">
            <div className="w-[320px] h-[320px] sm:w-[420px] sm:h-[420px] md:w-[520px] md:h-[520px] flex items-center justify-center">
              <Logo size="xlarge" className="animate-fade-in" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-tight mb-4 sm:mb-6">
            <span className={darkMode ? 'text-gray-300' : 'text-gray-900'}>
              DualTrack
            </span>{' '}
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent">
              OS
            </span>
          </h1>

          <p
            className={`text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-8 sm:mb-10 max-w-3xl ${
              darkMode ? 'text-gray-400' : 'text-gray-700'
            }`}
          >
            Your companion through every challenge, cycle, and chapter
          </p>

          {/* Primary CTA */}
          <button
            onClick={onEnter}
            className={`group relative px-7 py-3 text-base sm:text-lg font-bold transition-all duration-300 hover:scale-105 mb-6 ${
              darkMode ? 'bg-gray-900' : 'bg-white'
            }`}
            style={{
              border: '3px solid transparent',
              borderImage:
                'linear-gradient(135deg, #06b6d4 0%, #a855f7 33%, #ec4899 66%, #fb923c 100%) 1',
              boxShadow:
                '0 0 20px rgba(168, 85, 247, 0.45), 0 0 40px rgba(6, 182, 212, 0.25)',
            }}
          >
            <span className="flex items-center gap-2 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent">
              Start Your Day
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1 text-purple-500"
              />
            </span>
          </button>

          {/* Secondary CTA - Story */}
          <button
            onClick={onViewStory}
            className={`px-6 py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-105 ${
              darkMode
                ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40'
                : 'bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300'
            }`}
          >
            ✨ The Story Behind the App
          </button>
        </div>

        {/* BOTTOM — footer links + user status */}
        <div className="pb-4 sm:pb-6 mt-8 flex flex-col items-center gap-2">
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <button
              onClick={() => navigate('/roadmap')}
              className={`text-xs font-medium transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
            >
              Roadmap
            </button>
            <span className={`text-xs ${darkMode ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <button
              onClick={() => navigate('/pricing')}
              className={`text-xs font-medium transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
            >
              Pricing
            </button>
            <span className={`text-xs ${darkMode ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <button
              onClick={() => navigate('/faq')}
              className={`text-xs font-medium transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
            >
              FAQ
            </button>
            <span className={`text-xs ${darkMode ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <button
              onClick={() => navigate('/privacy')}
              className={`text-xs font-medium transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
            >
              Privacy
            </button>
          </div>
          {user && (
            <div className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Signed in as {user.email}
            </div>
          )}
        </div>
      </div>

      {/* Animation */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.9s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
